import React, { useState, useContext, useEffect } from "react";
import "./index.scss";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import { AccountContext } from "../../components";
import { Helmet } from "react-helmet-async";
import {
  CorrectIcon,
  FacebookIcon2,
  GoogleIcon,
  Grigora,
  EmailIcon,
} from "../../assets/ui-icons";
import { PulseLoader } from "react-spinners";
import Toast from "../../components/toast/toast";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { AnimatePresence, motion } from "framer-motion";
import ResendAction from "../../components/resend-action";
import { faEnvelope } from "@fortawesome/free-regular-svg-icons";
import { faChevronLeft } from "@fortawesome/free-solid-svg-icons";
import { INPUT_LIMITS } from "../../components/input-limits";
import { useTracking } from "../../components/tracking";
import { APP_NAME, WEBCLIP_URL } from "../../components/app";
import { WhiteLabelContext } from "../../components/whitelabel";

function Login() {
  const { __whitelabel__, isWhitelabel } = useContext(WhiteLabelContext) ?? {};

  const [isContinueWithEmail, setIsContinueWithEmail] = useState(
    isWhitelabel ? true : false,
  );
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [isLoggingIn, setIsLoggingIn] = useState(false);

  const [stage, setStage] = useState("");
  const [verificationcode, setVerificationCode] = useState("");

  const {
    authenticate,
    signInFederated,
    verifyUserAccount,
    sendUserVerificationCode,
    authenticateWithTokens,
    setIsLoggedInUsingAdmin,
  } = useContext(AccountContext) ?? {};
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { sendCustomEvent } = useTracking();

  const [isAttemptingToLoginUsingTokens, setIsAttemptingToLoginUsingTokens] =
    useState(false);

  const [returnTo] = useState(searchParams.get("return"));
  const returnToParsed = returnTo?.replace(
    // Replace | with & to make it a valid query string
    /\|/g,
    "&",
  );

  useEffect(() => {
    // Get params from search params
    const AccessToken = searchParams.get("AccessToken");
    const IdToken = searchParams.get("IdToken");
    const TokenType = searchParams.get("TokenType");
    const ExpiresIn = searchParams.get("ExpiresIn");

    const username = searchParams.get("username");

    if (AccessToken && IdToken && TokenType && ExpiresIn) {
      setIsAttemptingToLoginUsingTokens(true);
      authenticateWithTokens(
        username,
        AccessToken,
        IdToken,
        "",
        TokenType,
        ExpiresIn,
      )
        .then((data) => {
          if (returnToParsed) {
            if (returnToParsed?.startsWith("http")) {
              window.location = returnToParsed;
              return;
            }
            navigate(returnToParsed);
          } else {
            navigate("/");
          }
          setIsLoggedInUsingAdmin(true);
          setIsLoggingIn(false);
        })
        .catch((err) => {
          var code = err.code;

          if (code === "UserNotConfirmedException") {
            setIsLoggingIn(false);

            // sendUserVerificationCode(email);

            toast.error(
              <Toast
                title={"Error"}
                description={"Account not verified. Reset your password."}
              />,
            );
          } else {
            console.log(err);
            setIsLoggingIn(false);
            toast.error(
              <Toast
                title={"Error"}
                description={"Incorrect email or password."}
              />,
            );
          }
        });
    }
  }, []);

  const onSubmit = (event) => {
    console.log("called");
    if (isLoggingIn) {
      event.preventDefault();
      return;
    }

    setIsLoggingIn(true);
    event.preventDefault();

    if (email === "" && password === "") {
      setIsLoggingIn(false);
      toast.error(
        <Toast
          title={"Error"}
          description={"Please enter email and password."}
        />,
      );
      return;
    } else if (email === "") {
      setIsLoggingIn(false);
      toast.error(
        <Toast title={"Error"} description={"Please enter email."} />,
      );
      return;
    } else if (password === "") {
      setIsLoggingIn(false);
      toast.error(
        <Toast title={"Error"} description={"Please enter password."} />,
      );
      return;
    }

    if (stage === "verification") {
      verifyUserAccount(email, verificationcode)
        .then((data) => {
          toast.success(<Toast title={"Account Verified."} />, {
            icon: <CorrectIcon />,
          });

          // Authenticate user here

          authenticate(email, password)
            .then((data) => {
              // navigate("/");
              setIsLoggingIn(false);
            })
            .catch((err) => {
              console.log(err);
              toast.error(
                <Toast title={"Error"} description={"Something went wrong"} />,
              );
              setIsLoggingIn(false);
            });
        })
        .catch((err) => {
          console.log(err);

          var code = err.code;

          if (
            code === "ExpiredCodeException" ||
            code === "CodeMismatchException"
          ) {
            toast.error(
              <Toast title={"Error"} description={"Invalid Code."} />,
            );
          } else {
            toast.error(
              <Toast title={"Error"} description={"Some error ocurred."} />,
            );
          }

          setIsLoggingIn(false);
        });

      return;
    }

    authenticate(email, password)
      .then((data) => {
        setIsLoggingIn(false);
        if (returnToParsed) {
          navigate(returnToParsed);
        } else {
          navigate("/");
        }
      })
      .catch((err) => {
        console.log(err.code);

        var code = err.code;

        if (code === "UserNotConfirmedException") {
          setIsLoggingIn(false);
          setStage("verification");

          sendUserVerificationCode(email);

          toast.error(
            <Toast
              title={"Error"}
              description={"Account not verified. Verify first."}
            />,
          );
        } else {
          setIsLoggingIn(false);
          toast.error(
            <Toast
              title={"Error"}
              description={"Incorrect email or password."}
            />,
          );
        }
      });
  };

  return (
    <>
      <Helmet>
        <meta charSet="utf-8" />
        <title>Login - {__whitelabel__("platformName", APP_NAME)}</title>
      </Helmet>
      <div className="overflow-hidden bg-white md:bg-gray-50 flex flex-col items-center m-auto w-full min-h-[100svh] md:min-h-[100vh] gap-2 justify-center px-2 login-container">
        {isAttemptingToLoginUsingTokens ? (
          <>
            <div className="flex flex-col items-center justify-center gap-2">
              <PulseLoader color="black" />
            </div>
          </>
        ) : (
          <>
            <div
              style={{
                marginTop: isContinueWithEmail ? undefined : "0px",
              }}
              className="relative bg-white px-3 py-5 md:mt-[100px] md:py-10 md:px-8 rounded-[21px] md:shadow-lg mx-auto max-w-full w-[400px] flex flex-col items-center"
            >
              <div className="absolute md:top-[-120px] top-[-50px] mt-[20px] md:mt-[0px] mb-1 md:mb-4 flex ml-[20px]">
                <img
                  src={__whitelabel__("icon", WEBCLIP_URL)}
                  className="w-[100px] h-[100px] text-ui-blue"
                />
                {/* <span className="relative top-[12px] left-[-15px] rounded-[5px] px-[12px] py-[1px] text-[10px] h-fit border border-ui-blue">
              BETA
            </span> */}
              </div>
              <h1 className="text-[20px] font-semibold md:mt-0 mt-10 mb-2 md:mb-1">
                Welcome Back!
              </h1>
              {isContinueWithEmail ? (
                <>
                  <p className="text-[14px] mb-6 text-gray-500 text-center">
                    We missed you! Please enter your details.
                  </p>
                  <form onSubmit={onSubmit} className="w-full">
                    <div className="mb-6">
                      <label htmlFor="email" className="input-label">
                        Email
                      </label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        value={email}
                        onChange={(e) => {
                          setEmail(e.target.value);
                        }}
                        onBlur={(e) => {
                          // Tracking
                          sendCustomEvent("login_entered_email", "login");
                        }}
                        placeholder="Enter your Email"
                        className="input-field border text-grigora-base focus:border-ui-blue"
                      />
                    </div>
                    <div className="mb-4">
                      <label htmlFor="password" className="input-label">
                        Password
                      </label>
                      <input
                        type="password"
                        id="password"
                        name="password"
                        value={password}
                        onChange={(e) => {
                          setPassword(e.target.value);
                        }}
                        onBlur={(e) => {
                          // Tracking
                          sendCustomEvent("login_entered_password", "login");
                        }}
                        placeholder="Enter your Password"
                        className="input-field  text-grigora-base focus:border-ui-blue"
                      />
                    </div>
                    {stage === "verification" && (
                      <div className="mb-4">
                        <div className="w-full flex gap-2">
                          <label
                            htmlFor="verificationcode"
                            className="ml-[20px] block text-sm font-medium text-gray-700"
                          >
                            Verification Code (Email)
                          </label>
                          <ResendAction
                            action={() => {
                              return sendUserVerificationCode(email);
                            }}
                            successNotification={"Verification Code Sent."}
                            errorNotification={
                              "Some error ocurred. Try again after sometime."
                            }
                            timeout={30}
                          />
                        </div>
                        <input
                          required={true}
                          type="text"
                          id="verificationcode"
                          minLength={
                            INPUT_LIMITS["verification-code"].minlength
                          }
                          maxLength={
                            INPUT_LIMITS["verification-code"].maxlength
                          }
                          name="verificationcode"
                          value={verificationcode}
                          onChange={(e) => {
                            setVerificationCode(e.target.value);
                          }}
                          placeholder="Enter Verfication Code"
                          className="input-field  text-grigora-base focus:border-ui-blue"
                        />
                      </div>
                    )}
                    <div className="w-full flex">
                      <Link
                        className="text-ui-blue text-end text-[14px] ml-auto"
                        to="/forgot"
                        onClick={(e) => {
                          sendCustomEvent(
                            "login_forgot_password_clicked",
                            "login",
                          );
                        }}
                      >
                        Forgot Password?
                      </Link>
                    </div>
                    <button
                      type="submit"
                      className="grigora-login-btn w-full py-2 px-4 mt-5 text-[16px] rounded-[21px] bg-ui-blue h-[50px] text-white duration-300 outline-none"
                    >
                      {isLoggingIn ? (
                        <>
                          <PulseLoader color="#ffffff" size={8} />
                        </>
                      ) : (
                        "Sign In"
                      )}
                    </button>
                  </form>
                </>
              ) : (
                <></>
              )}

              {isContinueWithEmail ? (
                !isWhitelabel && (
                  <>
                    <div className="flex flex-wrap mt-6 w-full gap-2 justify-center items-center">
                      <span
                        onClick={() => {
                          setIsContinueWithEmail(false);
                          // Tracking
                          sendCustomEvent(
                            "login_clicked_login_button",
                            "login",
                          );
                        }}
                        className="cursor-pointer duration-300 hover:text-gray-600 text-gray-500 text-[14px] flex gap-2 items-center"
                      >
                        <FontAwesomeIcon
                          icon={faChevronLeft}
                          className="cursor-pointer w-[10px] h-[10px]"
                        />
                        Back
                      </span>
                    </div>
                  </>
                )
              ) : (
                <>
                  <div className="federated-btn">
                    <button
                      onClick={(e) => {
                        signInFederated("Google", () => {
                          if (returnToParsed) {
                            navigate(returnToParsed);
                          } else {
                            navigate("/");
                          }
                        });
                        // Tracking
                        sendCustomEvent("login_google", "login");
                      }}
                      className="federated-btn__children  md:gap-[12%] "
                    >
                      <GoogleIcon className="cursor-pointer w-[22px] h-[22px]" />
                      <span className="text-[14px] text-gray-700">
                        Continue with Google
                      </span>
                    </button>
                  </div>
                  <div className="federated-btn">
                    <button
                      onClick={(e) => {
                        signInFederated("Facebook", () => {
                          if (returnToParsed) {
                            navigate(returnToParsed);
                          } else {
                            navigate("/");
                          }
                        });
                        // Tracking
                        sendCustomEvent("login_facebook", "login");
                      }}
                      className="federated-btn__children md:gap-[12%] "
                    >
                      <FacebookIcon2 className="cursor-pointer w-[22px] h-[22px]" />
                      <span className="text-[14px] text-gray-700">
                        Continue with Facebook
                      </span>
                    </button>
                  </div>

                  <div className="federated-btn">
                    <button
                      onClick={(e) => {
                        setIsContinueWithEmail(true);
                        // Tracking
                        sendCustomEvent("login_email", "login");
                      }}
                      className="federated-btn__children md:gap-[12%] bg-black bg-opacity-90"
                    >
                      {/* <EmailIcon className="cursor-pointer w-[22px] h-[22px]" /> */}
                      <span className="text-[14px] ml-[25%] md:ml-[21%] text-gray-200">
                        Continue with Email
                      </span>
                    </button>
                  </div>
                </>
              )}
              <p className="text-[14px] text-gray-500 mt-10 text-center">
                Don&apos;t have an account?{" "}
                <Link
                  className="text-ui-blue underline"
                  to={`/sign-up${
                    returnToParsed
                      ? `?return=${encodeURIComponent(returnToParsed)}`
                      : ""
                  }`}
                >
                  Sign Up
                </Link>
              </p>
            </div>
          </>
        )}
      </div>
    </>
  );
}

export default Login;
